import { Flex, Input, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useSuspenseQuery } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { useController } from 'react-hook-form';

import { BaseResearchPerfEvalSelectModal } from '@/module/ResearchPerfEval/form/select/BaseResearchPerfEvalSelectModal';
import { getResearchPerfEvalCriteriaPossibleItemsOptions } from '@/module/ResearchPerfEval/model/ResearchPerfEvalQuery';

import { Id } from '@feature/utils';

export interface BaseResearchPerfEvalSelectControl<T> {
  renderItem: (item: T) => ReactNode;
  onView: (item: T) => void;

  getTitle: (item: T) => string;
  getId: (item: T) => number;

  evaluationId: Id;
  authorId: Id;
  orderNo: number;
  name: string;

  label: string;
  isSubmitting: boolean;

  onFilter: (item: T, search: string) => boolean;
  alreadySelectedIds: number[];
}

export function BaseResearchPerfEvalSelectControl<T>(props: BaseResearchPerfEvalSelectControl<T>) {
  const {
    renderItem,
    getTitle,
    getId,
    evaluationId,
    authorId,
    orderNo,
    name,
    isSubmitting,
    label,
    onView,
    onFilter,
    alreadySelectedIds,
  } = props;
  const {
    field,
    fieldState: { error },
  } = useController({ name });
  const [opened, { open, close }] = useDisclosure(false);

  const { data: possibleOptions } = useSuspenseQuery(
    getResearchPerfEvalCriteriaPossibleItemsOptions<T>(evaluationId, authorId, orderNo),
  );

  return (
    <Input.Wrapper
      withAsterisk
      label={label}
      error={error?.message}
    >
      <Flex align="center">
        <Input
          flex={1}
          onClick={open}
          disabled={isSubmitting}
          component="button"
          type="button"
          pointer
        >
          {field.value != null ? (
            <Text
              size="sm"
              lineClamp={1}
            >
              {getTitle(field.value)}
            </Text>
          ) : (
            <Text
              c="dimmed"
              size="sm"
            >
              fără selecție
            </Text>
          )}
        </Input>
        {field.value != null ? <Input.ClearButton onClick={() => field.onChange(null)} /> : undefined}
      </Flex>
      {opened && (
        <BaseResearchPerfEvalSelectModal
          close={close}
          list={possibleOptions}
          renderItem={renderItem}
          onSelect={item => field.onChange({ id: getId(item), title: getTitle(item) })}
          onView={onView}
          onFilter={onFilter}
          label={label}
          getId={getId}
          getTitle={getTitle}
          selectedIds={alreadySelectedIds}
        />
      )}
    </Input.Wrapper>
  );
}
